import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode, Fragment as _Fragment, withCtx as _withCtx, unref as _unref, createSlots as _createSlots } from "vue";
const _hoisted_1 = { class: "td-v2" };
const _hoisted_2 = {
    key: 0,
    class: "merchant-hero"
};
const _hoisted_3 = {
    key: 1,
    class: "merchant-hero"
};
const _hoisted_4 = { class: "merchant-name" };
const _hoisted_5 = {
    key: 3,
    class: "category-block"
};
const _hoisted_6 = ["src"];
const _hoisted_7 = ["href"];
const _hoisted_8 = { class: "switch-top" };
const _hoisted_9 = { class: "switch-top-left" };
const _hoisted_10 = { class: "switch-avatars" };
const _hoisted_11 = ["src"];
const _hoisted_12 = ["src"];
const _hoisted_13 = {
    key: 11,
    class: "summary-block"
};
const _hoisted_14 = { class: "summary-heading" };
const _hoisted_15 = {
    key: 12,
    class: "tagSection"
};
const _hoisted_16 = { class: "drawer-tx-wrap" };
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useTransactions } from '@/composables/useTransactions';
import { usePolling } from '@/composables/usePolling';
import { getSpendingAnalysis, clearSpendingAnalysisCache, formatTimestampString, findCurrentMonthIndex, groupTransactionsByDay } from '@/composables/useSpendingAnalysis';
import HeaderBlock from './elements/HeaderBlock.vue';
import CategoryLozenge from './elements/CategoryLozenge.vue';
import SpendingSummaryCard from './elements/SpendingSummaryCard.vue';
import TransactionsList from './elements/TransactionsList.vue';
import RecentTransactionsSection from './elements/RecentTransactionsSection.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import ChangeCategoryDrawer from './elements/ChangeCategoryDrawer.vue';
import BudgetSummaryCard from './elements/BudgetSummaryCard.vue';
import TagInput from './elements/TagInput.vue';
import WellPanel from './elements/WellPanel.vue';
import RetailDiscountCard from './elements/RetailDiscountCard.vue';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useVouchers } from '@/composables/useVouchers';
import { selectedFilter, selectedSubCategory, selectedSubCategoryColour, selectedMerchant, snapToSpendingCycle, cameFromTransactionDetail, } from './elements/spendingAnalysisFilterState';
import { getMerchantLogoUrl, getMerchantLogoStyle, onMerchantLogoError, isWellKnownLogosOnly } from '@/utils/helpers';
// ---------- Pure helpers ----------
// [startOfMonth 5 months ago, now] — gives 6 monthly buckets inclusive of the current month.
export default /*@__PURE__*/ _defineComponent({
    __name: 'TransactionDetailPage',
    setup(__props) {
        function sixMonthRange() {
            const now = new Date();
            const fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1));
            const toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
            return { fromDate, toDate };
        }
        // Map the compact spending-analysis transaction shape to the richer shape
        // TransactionsList expects (effectiveClassification object, merchant object,
        // timestampString for the date-separator header).
        function reshapeSpendingTx(apiTx) {
            return {
                id: apiTx.id,
                merchantName: apiTx.merchantName,
                merchant: apiTx.wellKnownMerchant ? { name: apiTx.merchantName } : null,
                wellKnownMerchant: apiTx.wellKnownMerchant,
                amount: apiTx.amount,
                timestamp: apiTx.timestamp,
                timestampString: formatTimestampString(apiTx.timestamp),
                customerTags: apiTx.tags ?? apiTx.customerTags,
                effectiveClassification: {
                    mgClassification: apiTx.classification,
                    mgSubClassification: apiTx.subClassification,
                    color: apiTx.color,
                    iconPath: apiTx.icon,
                },
            };
        }
        // ---------- Composables ----------
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const transactionsApi = useTransactions();
        const { getCustomerTags, clearCustomerTagsCache } = useCustomer();
        const { getBankingInfo, clearBankingInfo } = useBanking();
        const { getRecommendedVouchers, getAvailableVouchers } = useVouchers();
        const { poll } = usePolling('banking');
        const wellKnownLogosOnlyGlobal = getCurrentInstance()?.appContext.config.globalProperties.$wellKnownLogosOnly;
        // ---------- Template refs ----------
        const categoryDrawer = useTemplateRef('categoryDrawer');
        const transactionsDrawerOpen = ref(false);
        // ---------- Reactive state ----------
        // Single source of truth: always fetched via the direct API. The server caches,
        // so this is cheap and avoids the drift/edge-cases of scanning recentTransactions.
        const transaction = ref(null);
        // Monthly spend for this transaction's sub-classification over the last 6
        // months, fetched from the spending analysis API. Populated by fetchSummary().
        const monthlyData = ref([]);
        const summaryLoading = ref(false);
        // Transactions for this merchant (within the same 12-month window as the chart),
        // grouped by date for TransactionsList. Populated by fetchSummary().
        const merchantTxGroups = ref([]);
        // `switchable` comes from the spending-analysis response (the direct
        // transaction endpoint doesn't return it), so capture it during fetchSummary.
        const switchableFromAnalysis = ref(false);
        // Budget (plan) details for this transaction's sub-category, captured during
        // fetchSummary. Populated when the matching category has a plan.
        const categoryBudget = ref(null);
        // Customer hashtags
        const selectedTags = ref([]);
        const availableTags = ref(null);
        // Matching recommended voucher for this merchant — populated via getRecommendedVouchers
        // so we can show the user's spend at this merchant and the potential saving.
        const merchantRecommendedVoucher = ref(null);
        // ---------- Computeds: identity ----------
        const transactionId = computed(() => String(route.params.id ?? ''));
        const merchantName = computed(() => {
            const t = transaction.value;
            if (!t)
                return '';
            return t.merchant?.name ?? t.merchantName ?? '';
        });
        const categoryName = computed(() => {
            const t = transaction.value;
            if (!t)
                return 'Uncategorised';
            const c = t.effectiveClassification;
            return c?.mgSubClassification || c?.mgClassification || 'Uncategorised';
        });
        // Merchant trend uses a fixed purple regardless of the transaction's category.
        const barColour = computed(() => '#413655');
        // ---------- Computeds: hero display ----------
        const isIncome = computed(() => (transaction.value?.amount ?? 0) > 0);
        const amountDisplay = computed(() => {
            const amt = transaction.value?.amount ?? 0;
            const formatted = state.formatter.format(Math.abs(amt));
            return isIncome.value ? '+' + formatted : formatted;
        });
        const descriptionDisplay = computed(() => {
            const desc = transaction.value?.description ?? '';
            return desc && desc !== merchantName.value ? desc : '';
        });
        const fullDateDisplay = computed(() => {
            const ts = transaction.value?.timestamp;
            if (!ts)
                return '';
            return new Date(ts).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        });
        const accountDisplay = computed(() => {
            const id = transaction.value?.bankAccountID;
            if (!id)
                return '';
            const account = (state.bankInfo?.accounts ?? []).find((a) => a.id === id);
            if (!account)
                return '';
            return account.bank?.prettyName ?? account.bank?.name ?? account.bankDisplayName ?? '';
        });
        const moneyDirectionDisplay = computed(() => (isIncome.value ? 'Money in' : 'Money out'));
        const spendingAnalysisDisplay = computed(() => (transaction.value?.excludeFromAnalysis ? 'Excluded' : 'Included'));
        const rawDescriptionDisplay = computed(() => transaction.value?.description ?? '');
        const detailsWellData = computed(() => {
            const rows = [
                { order: 1, label: 'Date', value: fullDateDisplay.value },
                { order: 2, label: 'Account', value: accountDisplay.value },
                { order: 3, label: 'Transaction', value: moneyDirectionDisplay.value },
                { order: 4, label: 'Spending Analysis', value: spendingAnalysisDisplay.value },
            ];
            if (rawDescriptionDisplay.value) {
                rows.push({ order: 5, label: 'Description', value: rawDescriptionDisplay.value });
            }
            return rows;
        });
        // ---------- Computeds: retail-card discount ----------
        // Find an available retail-card voucher matching this transaction's merchant.
        // `state.availableVouchers` is loaded on mount; we match by merchant name
        // (the most reliable cross-source identifier).
        const merchantDiscount = computed(() => {
            const name = merchantName.value;
            if (!name)
                return null;
            const vouchers = state.availableVouchers ?? [];
            const match = vouchers.find((v) => v.merchant?.name?.toLowerCase() === name.toLowerCase());
            if (!match || !(match.effectiveDiscountPercentage > 0))
                return null;
            return match;
        });
        const discountSpendDisplay = computed(() => {
            const spent = merchantRecommendedVoucher.value?.spent;
            if (spent === undefined || spent === null)
                return '';
            return state.formatter.format(Math.abs(spent));
        });
        const discountSavingDisplay = computed(() => {
            const saving = merchantRecommendedVoucher.value?.saving;
            if (saving === undefined || saving === null)
                return '';
            return state.formatter.format(Math.abs(saving));
        });
        // ---------- Computeds: chart ----------
        const currentMonthIndex = computed(() => findCurrentMonthIndex(monthlyData.value));
        // ---------- Computeds: switch-to-save ----------
        const showSwitchToSave = computed(() => transaction.value?.switchable === true || switchableFromAnalysis.value);
        const switchToSaveHref = computed(() => {
            const person = state.customer?.person;
            const email = person?.email ?? '';
            const postCode = person?.addresses?.[0]?.postCode ?? '';
            return 'https://moneyguided.aptap.co.uk?p1=' + email + '&p2=' + postCode;
        });
        // ---------- Computeds: merchant logo ----------
        const merchantLogoInput = computed(() => {
            const t = transaction.value;
            return {
                name: merchantName.value,
                wellKnown: !!t?.merchant || t?.wellKnownMerchant === true,
                iconPath: t?.effectiveClassification?.iconPath,
                iconColour: t?.effectiveClassification?.color,
                wellKnownLogosOnly: isWellKnownLogosOnly(wellKnownLogosOnlyGlobal),
            };
        });
        const merchantLogoUrl = computed(() => {
            const t = transaction.value;
            // Prefer an API-supplied logo URL when present; otherwise use the shared fallback chain.
            if (t?.merchant?.logoUrl)
                return t.merchant.logoUrl;
            return getMerchantLogoUrl(merchantLogoInput.value);
        });
        const merchantLogoStyle = computed(() => getMerchantLogoStyle(merchantLogoInput.value));
        // Full list of this merchant's transactions within the summary window.
        const allMerchantTransactions = computed(() => merchantTxGroups.value);
        // ---------- Fetch ----------
        function loadTransaction(id) {
            if (!id) {
                transaction.value = null;
                selectedTags.value = [];
                return;
            }
            if (transaction.value?.id === id)
                return;
            transaction.value = null;
            selectedTags.value = [];
            transactionsApi.getTransaction(id, (t) => {
                if (t?.id !== transactionId.value)
                    return;
                transaction.value = t;
                selectedTags.value = Array.isArray(t?.customerTags)
                    ? t.customerTags.map((ct) => ct.tag)
                    : [];
            });
        }
        // Fetch the last 6 months of spending filtered to this merchant. The response
        // powers both the chart (timeSlicedBreakdowns) and the merchant-transactions
        // list (categories[].spendByMerchant[].transactions).
        function fetchSummary() {
            const t = transaction.value;
            if (!t)
                return;
            const subName = t.effectiveClassification?.mgSubClassification;
            // Spending analysis returns the raw bank descriptor in `merchantName`, while
            // the full transaction may also expose a normalised `merchant.name`. Match
            // against either so the list isn't empty when the two forms differ.
            const merchantNames = new Set([t.merchantName, t.merchant?.name].filter((n) => !!n));
            const merchantForFetch = t.merchantName || t.merchant?.name || '';
            summaryLoading.value = true;
            const { fromDate, toDate } = sixMonthRange();
            getSpendingAnalysis(fromDate, toDate, { merchant: merchantForFetch, snapToSpendingCycle: snapToSpendingCycle.value }, (result) => {
                summaryLoading.value = false;
                const windows = result?.timeSlicedBreakdowns ?? [];
                monthlyData.value = windows.map((w) => ({
                    label: w.label,
                    // Use whichever side is non-zero so income categories (where totalOut=0) still render bars.
                    value: Math.max(w.totalOut ?? 0, w.totalIn ?? 0),
                    fromDate: w.fromDate,
                    toDate: w.toDate,
                }));
                // Collect transactions for this merchant across the sub-category breakdowns.
                const flat = [];
                const cats = result?.categories ?? [];
                switchableFromAnalysis.value = false;
                // Find a plan for this sub-category. Use the current month's bar value as
                // currentSpend (budgets are monthly). Reset date is the end of the current
                // calendar month — the 6-month window's toDate would be misleading here.
                const matchingCat = cats.find((c) => c.plan && c.subCategory === subName);
                if (matchingCat) {
                    const idx = findCurrentMonthIndex(monthlyData.value);
                    const currentSpend = Math.abs((idx !== undefined ? monthlyData.value[idx]?.value : 0) ?? 0);
                    const now = new Date();
                    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    categoryBudget.value = {
                        categoryName: matchingCat.subCategory,
                        currentSpend,
                        planAmount: matchingCat.plan.amount,
                        resetDate: monthEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }),
                    };
                }
                else {
                    categoryBudget.value = null;
                }
                for (const cat of cats) {
                    const merchants = cat?.spendByMerchant ?? [];
                    for (const m of merchants) {
                        if (!m?.merchantName || !merchantNames.has(m.merchantName))
                            continue;
                        for (const tx of (m.transactions ?? [])) {
                            if (tx.id === t.id && tx.switchable === true)
                                switchableFromAnalysis.value = true;
                            flat.push(reshapeSpendingTx(tx));
                        }
                    }
                }
                merchantTxGroups.value = groupTransactionsByDay(flat);
            });
        }
        // ---------- Event handlers ----------
        // Clicking another transaction in either list navigates to that transaction's
        // detail page instead of the legacy iframe modal. A click on the current
        // transaction is a no-op.
        function onTransactionClick(t) {
            if (!t?.id || t.id === transactionId.value)
                return;
            transactionsDrawerOpen.value = false;
            router.push('/TransactionDetail/' + t.id);
        }
        function onDiscountClick() {
            const voucherId = merchantDiscount.value?.id;
            if (voucherId) {
                router.push('/vouchers/amount/' + voucherId);
                return;
            }
            router.push('/shopvouchers');
        }
        function viewAllForMerchant() {
            selectedFilter.value = 'all';
            selectedSubCategory.value = '';
            selectedSubCategoryColour.value = '';
            selectedMerchant.value = merchantName.value;
            cameFromTransactionDetail.value = true;
            router.push('/SpendingAnalysis');
        }
        function onDrawerTagChange(tags) {
            handleTagsChange(tags.map(tag => ({ tag })));
        }
        function onCategoryApply(payload) {
            const t = transaction.value;
            if (!payload.classification || !t?.id)
                return;
            const pollForChange = () => {
                const originalId = transaction.value?.effectiveClassification?.id ?? null;
                const originalExclude = transaction.value?.excludeFromAnalysis ?? false;
                let latestFetched = null;
                poll({
                    remaining: 20,
                    interval: 1000,
                    fetch: (cb) => {
                        transactionsApi.getTransaction(t.id, (fresh) => {
                            latestFetched = fresh;
                            cb();
                        });
                    },
                    onPoll: () => { },
                    shouldStop: () => {
                        if (!latestFetched)
                            return false;
                        const idChanged = latestFetched.effectiveClassification?.id !== originalId;
                        const excludeChanged = (latestFetched.excludeFromAnalysis ?? false) !== originalExclude;
                        return idChanged || excludeChanged;
                    },
                    onFinish: () => {
                        if (latestFetched)
                            transaction.value = latestFetched;
                        clearSpendingAnalysisCache();
                        getBankingInfo(null, true);
                        categoryDrawer.value?.closeDrawer();
                    },
                });
            };
            // Ensure the helper has a merchantName to dedupe / build a rule from,
            // even when the transaction itself doesn't carry one.
            const txForApply = { ...t, merchantName: t.merchantName || merchantName.value };
            transactionsApi.applyCategoryToTransactions([txForApply], payload, pollForChange);
        }
        function handleTagsChange(tags) {
            const t = transaction.value;
            if (!t?.id)
                return;
            const existing = (t.customerTags ?? []);
            const updatedTags = tags.map((newTag) => {
                const text = newTag.tag || newTag;
                const match = existing.find((ct) => ct.tag === text);
                return match ? { tag: match.tag, id: match.id } : { tag: text };
            });
            selectedTags.value = updatedTags.map(u => u.tag);
            transactionsApi.updateTransactionTags(t.bankAccountID, t.id, updatedTags, () => {
                clearCustomerTagsCache();
                clearBankingInfo();
                clearSpendingAnalysisCache();
                getCustomerTags(() => {
                    availableTags.value = (state.customerTags ?? []).map((ct) => ct.tag);
                });
                transactionsApi.getTransaction(t.id, (fresh) => {
                    if (fresh?.id === transactionId.value)
                        transaction.value = fresh;
                });
            });
        }
        function onLogoError(event) {
            onMerchantLogoError(event, merchantLogoInput.value);
        }
        function openCategoryDrawer() {
            nextTick(() => {
                categoryDrawer.value?.openDrawer();
            });
        }
        function openTransactionsDrawer() {
            transactionsDrawerOpen.value = true;
        }
        // ---------- Lifecycle ----------
        onMounted(() => {
            // Defer past App.vue's `route.path` watcher which strips this class on every nav.
            nextTick(() => document.body.classList.add('hide-nav-bar'));
            loadTransaction(transactionId.value);
            getCustomerTags(() => {
                availableTags.value = (state.customerTags ?? []).map((ct) => ct.tag);
            });
            if (!state.bankInfo)
                getBankingInfo();
            getAvailableVouchers();
        });
        onUnmounted(() => {
            document.body.classList.remove('hide-nav-bar');
        });
        // Re-load when navigating between transaction detail pages (route param change).
        watch(transactionId, loadTransaction);
        // When the transaction loads and has a retail-card discount, fetch the
        // recommended-voucher entry for this merchant so we can show spend / saving.
        watch(merchantDiscount, (d) => {
            if (!d) {
                merchantRecommendedVoucher.value = null;
                return;
            }
            const sysoMerchantId = d.merchant?.sysoMerchantId;
            if (!sysoMerchantId)
                return;
            getRecommendedVouchers(-365, (recs) => {
                console.log('[TransactionDetail] all recommended vouchers (12mo):', recs);
                merchantRecommendedVoucher.value = (recs ?? []).find((v) => v.merchant?.sysoMerchantId === sysoMerchantId) ?? null;
                console.log('[TransactionDetail] matched voucher for', d.merchant?.name, ':', merchantRecommendedVoucher.value);
            });
        });
        // Refetch the chart only when the fields it actually depends on change —
        // otherwise unrelated updates (e.g. saving a hashtag) would redraw the chart.
        watch(() => {
            const t = transaction.value;
            if (!t)
                return '';
            return `${t.effectiveClassification?.mgSubClassification ?? ''}|${t.merchantName ?? ''}|${t.merchant?.name ?? ''}`;
        }, () => fetchSummary());
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, { title: "Transaction" }),
                _createElementVNode("div", _hoisted_1, [
                    (!transaction.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createVNode(_component_v_skeleton_loader, {
                                type: "heading",
                                class: "skeleton-heading"
                            })
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("h2", _hoisted_4, _toDisplayString(merchantName.value), 1),
                            _createElementVNode("div", {
                                class: _normalizeClass(["transaction-amount", { 'amount-positive': isIncome.value }])
                            }, _toDisplayString(amountDisplay.value), 3)
                        ])),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 2,
                            type: "chip",
                            class: "skeleton-lozenge"
                        }))
                        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (merchantName.value)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    class: "view-merchant-link",
                                    onClick: viewAllForMerchant
                                }, [
                                    _createElementVNode("span", null, "View all " + _toDisplayString(merchantName.value), 1),
                                    _createElementVNode("img", {
                                        class: "view-merchant-arrow",
                                        src: require('@/assets/arrow-right.png'),
                                        alt: ""
                                    }, null, 8, _hoisted_6)
                                ]))
                                : _createCommentVNode("", true),
                            _createVNode(CategoryLozenge, {
                                categoryName: categoryName.value,
                                iconPath: transaction.value?.effectiveClassification?.iconPath,
                                iconColour: transaction.value?.effectiveClassification?.color,
                                onClick: openCategoryDrawer
                            }, null, 8, ["categoryName", "iconPath", "iconColour"]),
                            _createElementVNode("p", {
                                class: "category-hint",
                                onClick: openCategoryDrawer
                            }, "Not right? Change your category")
                        ])),
                    (merchantDiscount.value)
                        ? (_openBlock(), _createBlock(RetailDiscountCard, {
                            key: 4,
                            voucher: merchantDiscount.value,
                            onClick: onDiscountClick
                        }, {
                            default: _withCtx(() => [
                                (discountSpendDisplay.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _cache[2] || (_cache[2] = _createTextVNode(" You’ve spent ")),
                                        _createElementVNode("strong", null, _toDisplayString(discountSpendDisplay.value), 1),
                                        _cache[3] || (_cache[3] = _createTextVNode(" in the last 12 months. You could have saved up to ")),
                                        _createElementVNode("strong", null, _toDisplayString(discountSavingDisplay.value), 1),
                                        _cache[4] || (_cache[4] = _createTextVNode(" with Retail Cards. "))
                                    ], 64))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 1
                        }, 8, ["voucher"]))
                        : _createCommentVNode("", true),
                    (showSwitchToSave.value)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 5,
                            class: "switch-card",
                            href: switchToSaveHref.value
                        }, [
                            _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", _hoisted_9, [
                                    _createElementVNode("div", _hoisted_10, [
                                        _createElementVNode("img", {
                                            class: "switch-avatars-img",
                                            src: require('@/assets/SwitchAvatars.png'),
                                            alt: ""
                                        }, null, 8, _hoisted_11)
                                    ]),
                                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "switch-title" }, "Switch to save", -1))
                                ]),
                                _createElementVNode("img", {
                                    class: "switch-arrow",
                                    src: require('@/assets/arrow-right.png'),
                                    alt: ""
                                }, null, 8, _hoisted_12)
                            ]),
                            _cache[6] || (_cache[6] = _createElementVNode("p", { class: "switch-desc" }, "Compare deals available to you with our trusted partner, ApTap.", -1))
                        ], 8, _hoisted_7))
                        : _createCommentVNode("", true),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 6,
                            type: "image",
                            class: "skeleton-details"
                        }))
                        : (_openBlock(), _createBlock(WellPanel, {
                            key: 7,
                            data: detailsWellData.value,
                            class: "details-well"
                        }, null, 8, ["data"])),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 8,
                            type: "list-item-avatar-two-line, list-item-avatar-two-line",
                            class: "skeleton-tx"
                        }))
                        : (merchantTxGroups.value.length > 0)
                            ? (_openBlock(), _createBlock(RecentTransactionsSection, {
                                key: 9,
                                title: 'Transactions - ' + merchantName.value,
                                transactions: merchantTxGroups.value,
                                formatter: _unref(state).formatter,
                                today: _unref(state).today,
                                yesterday: _unref(state).yesterday,
                                limit: 4,
                                onTransactionClick: onTransactionClick,
                                categoryPills: true,
                                onViewMore: openTransactionsDrawer
                            }, null, 8, ["title", "transactions", "formatter", "today", "yesterday"]))
                            : _createCommentVNode("", true),
                    (!transaction.value || summaryLoading.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 10,
                            type: "image",
                            class: "skeleton-summary"
                        }))
                        : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("h3", _hoisted_14, _toDisplayString(merchantName.value) + " trend", 1),
                            (_openBlock(), _createBlock(SpendingSummaryCard, {
                                key: 'td-summary-' + merchantName.value,
                                mode: "category",
                                categoryLabel: merchantName.value,
                                monthlyData: monthlyData.value,
                                currentMonthIndex: currentMonthIndex.value,
                                barColour: barColour.value
                            }, _createSlots({ _: 2 }, [
                                (categoryBudget.value)
                                    ? {
                                        name: "footer",
                                        fn: _withCtx(() => [
                                            _createVNode(BudgetSummaryCard, {
                                                categoryName: categoryBudget.value.categoryName,
                                                currentSpend: categoryBudget.value.currentSpend,
                                                planAmount: categoryBudget.value.planAmount,
                                                resetDate: categoryBudget.value.resetDate,
                                                inline: "",
                                                onClick: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/SpendingPlan')))
                                            }, null, 8, ["categoryName", "currentSpend", "planAmount", "resetDate"])
                                        ]),
                                        key: "0"
                                    }
                                    : undefined
                            ]), 1032, ["categoryLabel", "monthlyData", "currentMonthIndex", "barColour"]))
                        ])),
                    _createVNode(AiChatBanner, { class: "aiChat" }),
                    (transaction.value && availableTags.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "tagHeading" }, "Add a transaction tag", -1)),
                            _createVNode(TagInput, {
                                selectableTags: availableTags.value,
                                modelValue: selectedTags.value,
                                maxTags: 5,
                                maxTagLength: 20,
                                placeholder: "Tag limit reached — remove one to add another",
                                chipsBelow: true,
                                onChange: handleTagsChange,
                                class: "tagInputBox"
                            }, null, 8, ["selectableTags", "modelValue"]),
                            _cache[8] || (_cache[8] = _createElementVNode("p", { class: "tagHelp" }, "Find your transaction tags on the Activity page. Try using them to group transactions from a holiday, or project.", -1))
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: transactionsDrawerOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((transactionsDrawerOpen).value = $event)),
                    title: 'Transactions - ' + merchantName.value,
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_16, [
                            (allMerchantTransactions.value.length > 0)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: true,
                                    showDateTotal: false,
                                    transactions: allMerchantTransactions.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    dividingLines: true,
                                    categoryPills: true,
                                    onTransactionClick: onTransactionClick
                                }, null, 8, ["transactions", "formatter", "today", "yesterday"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(ChangeCategoryDrawer, {
                    ref_key: "categoryDrawer",
                    ref: categoryDrawer,
                    merchantName: merchantName.value,
                    currentClassification: transaction.value?.effectiveClassification,
                    currentExcludeFromAnalysis: transaction.value?.excludeFromAnalysis ?? false,
                    selectableTags: availableTags.value ?? [],
                    currentTags: selectedTags.value,
                    onApply: onCategoryApply,
                    onTagChange: onDrawerTagChange
                }, null, 8, ["merchantName", "currentClassification", "currentExcludeFromAnalysis", "selectableTags", "currentTags"])
            ], 64));
        };
    }
});
